import React, { useState } from 'react';
import {
  ActionItem,
  MeetingAttendee,
  MeetingDecision,
  MeetingSummary,
  UpdateSummaryRequest,
} from '../../../../types/meeting';

// Sentinel for the owner picker's free-text option. An empty value means "no
// owner", so the fallback needs a value of its own.
const OWNER_FREE_TEXT = '__free_text__';

interface MeetingOutcomesEditorProps {
  summary: MeetingSummary;
  // Owner picker vocabulary. Picking an attendee is what makes an action
  // attributable to a person rather than to a typed string.
  attendees: MeetingAttendee[];
  approved: boolean;
  saving: boolean;
  error: string | null;
  // Set when someone else saved these outcomes first. The draft below is kept,
  // so this is a warning to act on, not a failure that discards work.
  conflict?: string | null;
  reloading?: boolean;
  // True once their version has been pulled in and the draft can be re-saved.
  reloaded?: boolean;
  // The server's current outcomes, shown read-only alongside the draft after a
  // reload so the user can reconcile the two before re-saving.
  latestSummary?: MeetingSummary | null;
  onReload?: () => void;
  onSave: (draft: UpdateSummaryRequest) => void;
  onCancel: () => void;
}

// Secretary-only inline editor for the meeting outcomes (Unit 10e). Holds local
// draft state; a single Save posts a full-replace payload. Array order is
// preserved, so reordering key points is meaning-bearing.
const MeetingOutcomesEditor: React.FC<MeetingOutcomesEditorProps> = ({
  summary,
  attendees,
  approved,
  saving,
  error,
  conflict,
  reloading,
  reloaded,
  latestSummary,
  onReload,
  onSave,
  onCancel,
}) => {
  const [overview, setOverview] = useState<string>(summary.overview || '');
  const [keyPoints, setKeyPoints] = useState<string[]>([...(summary.key_points || [])]);
  const [decisions, setDecisions] = useState<MeetingDecision[]>(
    (summary.decisions || []).map((d) => ({ ...d }))
  );
  const [actions, setActions] = useState<ActionItem[]>(
    (summary.action_items || []).map((a) => ({ ...a }))
  );

  const move = <T,>(list: T[], idx: number, delta: number): T[] => {
    const next = idx + delta;
    if (next < 0 || next >= list.length) return list;
    const copy = [...list];
    [copy[idx], copy[next]] = [copy[next], copy[idx]];
    return copy;
  };

  const handleSave = () => {
    onSave({
      overview: overview.trim() ? overview.trim() : null,
      key_points: keyPoints.map((p) => p.trim()).filter(Boolean),
      decisions: decisions
        .filter((d) => d.title.trim() || d.outcome.trim())
        .map((d) => ({
          title: d.title.trim(),
          moved_by: d.moved_by?.trim() ? d.moved_by.trim() : null,
          outcome: d.outcome.trim(),
        })),
      // Spread the original action rather than rebuilding it from known keys:
      // the save is a full replace, so any field not carried here is destroyed
      // server-side — which is how an action_id assigned on the last save would
      // be lost on this one.
      action_items: actions
        .filter((a) => a.description.trim())
        .map((a) => ({
          ...a,
          description: a.description.trim(),
          owner: a.owner?.trim() ? a.owner.trim() : null,
          due_date: a.due_date?.trim() ? a.due_date.trim() : null,
          status: a.status?.trim() ? a.status.trim() : 'open',
        })),
    });
  };

  // Picking an attendee sets the structured owner reference and mirrors their
  // name into the display field, so nothing downstream changes. Choosing free
  // text clears both references: an owner who is not a system user must not
  // leave a stale link behind pointing at whoever was picked before.
  const setActionOwner = (idx: number, choice: string) =>
    setActions((list) =>
      list.map((a, i) => {
        if (i !== idx) return a;
        const attendee = attendees.find((att) => att.attendee_id === choice);
        if (!attendee) {
          return { ...a, owner_user_id: null, owner_attendee_id: null };
        }
        return {
          ...a,
          owner: attendee.name,
          owner_user_id: attendee.user_id || null,
          owner_attendee_id: attendee.attendee_id,
        };
      })
    );

  return (
    <div className="meeting-panel outcomes-editor">
      <div className="meeting-panel-header">
        <div>
          <h3 className="meeting-panel-title">Edit outcomes</h3>
          <p className="meeting-panel-subtitle">Members see the published version.</p>
        </div>
      </div>

      {conflict && (
        <div className="outcomes-editor-notice outcomes-editor-notice-conflict" role="alert">
          <span className="outcomes-editor-notice-icon" aria-hidden="true">⚠</span>
          <span>
            {conflict} <strong>Your edits below have been kept</strong> and nothing was
            overwritten.{' '}
            {reloaded
              ? 'Their version is shown below — merge in anything you need, then save again to replace it.'
              : 'Reload to see their version before deciding what to keep.'}
          </span>
          {onReload && !reloaded && (
            <button
              type="button"
              className="meeting-btn meeting-btn-secondary meeting-btn-sm"
              onClick={onReload}
              disabled={reloading}
            >
              {reloading ? 'Reloading…' : 'Reload their version'}
            </button>
          )}
        </div>
      )}

      {conflict && reloaded && (
        <div className="outcomes-editor-theirs">
          <h4 className="outcomes-editor-section-title">Their saved version</h4>
          {latestSummary?.overview && <p className="outcomes-editor-theirs-text">{latestSummary.overview}</p>}
          {(latestSummary?.key_points || []).length > 0 && (
            <ul className="outcomes-editor-theirs-list">
              {latestSummary!.key_points.map((point, idx) => <li key={idx}>{point}</li>)}
            </ul>
          )}
          {(latestSummary?.decisions || []).map((dec, idx) => (
            <p className="outcomes-editor-theirs-text" key={`d${idx}`}>{dec.title} — {dec.outcome}</p>
          ))}
          {(latestSummary?.action_items || []).map((act, idx) => (
            <p className="outcomes-editor-theirs-text" key={`a${idx}`}>{act.description}{act.owner ? ` (${act.owner})` : ''}</p>
          ))}
        </div>
      )}

      {approved && (
        <div className="outcomes-editor-notice" role="status">
          <span className="outcomes-editor-notice-icon" aria-hidden="true">ⓘ</span>
          <span>
            Editing requires re-approval before export. Saving will <strong>reset approval</strong>.
          </span>
        </div>
      )}

      <div className="outcomes-editor-field">
        <label className="outcomes-editor-label" htmlFor="outcomes-overview">Overview</label>
        <textarea
          id="outcomes-overview"
          className="outcomes-editor-textarea"
          value={overview}
          onChange={(e) => setOverview(e.target.value)}
          placeholder="Summarize the meeting in 2–3 sentences."
        />
      </div>

      <div className="outcomes-editor-section">
        <div className="outcomes-editor-section-head">
          <h4 className="outcomes-editor-section-title">Key points</h4>
          <button type="button" className="meeting-btn meeting-btn-ghost meeting-btn-sm" onClick={() => setKeyPoints((p) => [...p, ''])}>
            + Add
          </button>
        </div>
        {keyPoints.length === 0 && (
          <div className="outcomes-editor-empty">No key points yet. Add the main takeaways from this meeting.</div>
        )}
        {keyPoints.map((point, idx) => (
          <div className="outcomes-editor-row" key={idx}>
            <input
              className="outcomes-editor-input"
              aria-label={`Key point ${idx + 1}`}
              value={point}
              onChange={(e) => setKeyPoints((p) => p.map((x, i) => (i === idx ? e.target.value : x)))}
              placeholder="Discussion point…"
            />
            <div className="outcomes-editor-iconbtn-group">
              <button type="button" className="outcomes-editor-iconbtn" aria-label="Move up" disabled={idx === 0} onClick={() => setKeyPoints((p) => move(p, idx, -1))}>↑</button>
              <button type="button" className="outcomes-editor-iconbtn" aria-label="Move down" disabled={idx === keyPoints.length - 1} onClick={() => setKeyPoints((p) => move(p, idx, 1))}>↓</button>
              <button type="button" className="outcomes-editor-iconbtn outcomes-editor-iconbtn-danger" aria-label="Remove key point" onClick={() => setKeyPoints((p) => p.filter((_, i) => i !== idx))}>✕</button>
            </div>
          </div>
        ))}
      </div>

      <div className="outcomes-editor-section">
        <div className="outcomes-editor-section-head">
          <h4 className="outcomes-editor-section-title">Decisions</h4>
          <button
            type="button"
            className="meeting-btn meeting-btn-ghost meeting-btn-sm"
            onClick={() => setDecisions((d) => [...d, { title: '', moved_by: '', outcome: '' }])}
          >
            + Add
          </button>
        </div>
        {decisions.length === 0 && (
          <div className="outcomes-editor-empty">No decisions recorded. Add decisions the group agreed on.</div>
        )}
        {decisions.map((dec, idx) => (
          <div className="outcomes-editor-card" key={idx}>
            <div className="outcomes-editor-field">
              <label className="outcomes-editor-label" htmlFor={`decision-${idx}-title`}>Decision title</label>
              <input
                id={`decision-${idx}-title`}
                className="outcomes-editor-input"
                value={dec.title}
                onChange={(e) => setDecisions((d) => d.map((x, i) => (i === idx ? { ...x, title: e.target.value } : x)))}
                placeholder="e.g. Adopt the AI transcription assistant"
              />
            </div>
            <div className="outcomes-editor-fields outcomes-editor-fields-decision">
              <div className="outcomes-editor-field">
                <label className="outcomes-editor-micro-label" htmlFor={`decision-${idx}-movedby`}>Moved by</label>
                <input
                  id={`decision-${idx}-movedby`}
                  className="outcomes-editor-input"
                  value={dec.moved_by || ''}
                  onChange={(e) => setDecisions((d) => d.map((x, i) => (i === idx ? { ...x, moved_by: e.target.value } : x)))}
                  placeholder="e.g. Kat Gref"
                />
              </div>
              <div className="outcomes-editor-field">
                <label className="outcomes-editor-micro-label" htmlFor={`decision-${idx}-outcome`}>Outcome</label>
                <input
                  id={`decision-${idx}-outcome`}
                  className="outcomes-editor-input"
                  value={dec.outcome}
                  onChange={(e) => setDecisions((d) => d.map((x, i) => (i === idx ? { ...x, outcome: e.target.value } : x)))}
                  placeholder="e.g. Carried"
                />
              </div>
              <button type="button" className="outcomes-editor-iconbtn outcomes-editor-iconbtn-danger" aria-label="Remove decision" onClick={() => setDecisions((d) => d.filter((_, i) => i !== idx))}>✕</button>
            </div>
          </div>
        ))}
      </div>

      <div className="outcomes-editor-section">
        <div className="outcomes-editor-section-head">
          <h4 className="outcomes-editor-section-title">Actions</h4>
          <button
            type="button"
            className="meeting-btn meeting-btn-ghost meeting-btn-sm"
            onClick={() => setActions((a) => [...a, { description: '', owner: '', due_date: '', status: 'open' }])}
          >
            + Add
          </button>
        </div>
        {actions.length === 0 && (
          <div className="outcomes-editor-empty">No action items yet. Add tasks with an owner and due date.</div>
        )}
        {actions.map((act, idx) => (
          // Keyed by action_id where the server has assigned one; the index is
          // the fallback only while it is still null, and is never used as an
          // identity anywhere else.
          <div className="outcomes-editor-card" key={act.action_id || idx}>
            <div className="outcomes-editor-field">
              <label className="outcomes-editor-label" htmlFor={`action-${idx}-desc`}>Action</label>
              <input
                id={`action-${idx}-desc`}
                className="outcomes-editor-input"
                value={act.description}
                onChange={(e) => setActions((a) => a.map((x, i) => (i === idx ? { ...x, description: e.target.value } : x)))}
                placeholder="e.g. Coordinate with the tech team on rollout"
              />
            </div>
            <div className="outcomes-editor-fields outcomes-editor-fields-action">
              <div className="outcomes-editor-field">
                <label className="outcomes-editor-micro-label" htmlFor={`action-${idx}-owner`}>Owner</label>
                <select
                  id={`action-${idx}-owner`}
                  className="outcomes-editor-input outcomes-editor-select"
                  value={act.owner_attendee_id || (act.owner ? OWNER_FREE_TEXT : '')}
                  onChange={(e) => setActionOwner(idx, e.target.value)}
                >
                  <option value="">Unassigned</option>
                  {attendees.map((att) => (
                    <option key={att.attendee_id} value={att.attendee_id}>
                      {att.name}
                    </option>
                  ))}
                  <option value={OWNER_FREE_TEXT}>Someone else…</option>
                </select>
                {/* Free text stays available for owners who are not attendees —
                    a team, an external adviser, a role. Such an action is
                    reported as having no confirmed owner rather than guessed at. */}
                {!act.owner_attendee_id && (
                  <input
                    className="outcomes-editor-input"
                    aria-label={`Owner name for action ${idx + 1}`}
                    value={act.owner || ''}
                    onChange={(e) => setActions((a) => a.map((x, i) => (i === idx ? { ...x, owner: e.target.value } : x)))}
                    placeholder="e.g. Finance team"
                  />
                )}
              </div>
              <div className="outcomes-editor-field">
                <label className="outcomes-editor-micro-label" htmlFor={`action-${idx}-due`}>Due date</label>
                <input
                  id={`action-${idx}-due`}
                  className="outcomes-editor-input"
                  type="date"
                  value={act.due_date || ''}
                  onChange={(e) => setActions((a) => a.map((x, i) => (i === idx ? { ...x, due_date: e.target.value } : x)))}
                />
              </div>
              <div className="outcomes-editor-field">
                <label className="outcomes-editor-micro-label" htmlFor={`action-${idx}-status`}>Status</label>
                <select
                  id={`action-${idx}-status`}
                  className="outcomes-editor-input outcomes-editor-select"
                  value={act.status || 'open'}
                  onChange={(e) => setActions((a) => a.map((x, i) => (i === idx ? { ...x, status: e.target.value } : x)))}
                >
                  <option value="open">Open</option>
                  <option value="in progress">In progress</option>
                  <option value="done">Done</option>
                  <option value="blocked">Blocked</option>
                </select>
              </div>
              <button type="button" className="outcomes-editor-iconbtn outcomes-editor-iconbtn-danger" aria-label="Remove action" onClick={() => setActions((a) => a.filter((_, i) => i !== idx))}>✕</button>
            </div>
          </div>
        ))}
      </div>

      <div className="outcomes-editor-footer">
        {error && <span className="outcomes-editor-error" role="alert">{error}</span>}
        <button type="button" className="meeting-btn meeting-btn-secondary meeting-btn-sm" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="meeting-btn meeting-btn-primary meeting-btn-sm" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
};

export default MeetingOutcomesEditor;
